import { FixedAssetsTotalTable } from '@/feature/financial-model/ecommerce/fixed-assets/api/types.ts'

export const STYLES_COMBINATIONS = {
  TITLE_GRAY_CELLS_TRANSPARENT: {
    title_cell: {
      bg_color: `white`,
      value: { color: `dark_gray`, weight: `medium` }
    },
    period_cell: {
      bg_color: `white`,
      value: { color: `transparent`, weight: `medium` }
    }
  },
  TITLE_WHITE_CELLS_GRAY: {
    title_cell: {
      bg_color: `white`,
      value: { color: `dark_gray`, weight: `medium` }
    },
    period_cell: {
      bg_color: `gray`,
      value: { color: `transparent`, weight: `normal` }
    }
  },
  TITLE_BLACK_CELLS_TRANSPARENT: {
    title_cell: {
      bg_color: `white`,
      value: { color: `black`, weight: `medium` }
    },
    period_cell: {
      bg_color: `white`,
      value: { color: `transparent`, weight: `medium` }
    }
  },
  LIGHT_BLUE: {
    title_cell: {
      bg_color: `light_blue`,
      value: { color: `black`, weight: `semibold` }
    },
    period_cell: {
      bg_color: `light_blue`,
      value: { color: `black`, weight: `semibold` }
    }
  },
  DARK_BLUE: {
    title_cell: {
      bg_color: `dark_blue`,
      value: { color: `black`, weight: `semibold` }
    },
    period_cell: {
      bg_color: `dark_blue`,
      value: { color: `black`, weight: `semibold` }
    }
  }
} as const

export const TABLE_TYPES = {
  plain: `plain`,
  parent: `parent`
} as const

const PREV_YEAR = 2024
const CURRENT_YEAR = 2025

// this is also used as a base object for
export const FIXED_ASSETS_TOTAL_RESULTS_MOCK: FixedAssetsTotalTable<'yearly'> = {
  available_tables: [`intangible_assets`, `tangible_assets`, `financial_assets`, `total_fixed_assets`],

  // Intangible Assets Table
  intangible_assets: {
    table_type: TABLE_TYPES.plain,
    table_level: 0,
    table_name: `Intangible Assets`,
    available_rows: [`head`, `gross_book_value`, `a_and_d_rate`, `net_book_value`],
    head: {
      styles: {
        app: STYLES_COMBINATIONS.TITLE_GRAY_CELLS_TRANSPARENT
      },
      name: `Intangible Assets`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 0, type: `blank` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    gross_book_value: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Gross Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    a_and_d_rate: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Accumulated Amortization`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    net_book_value: {
      styles: { app: STYLES_COMBINATIONS.LIGHT_BLUE },
      name: `Net Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    }
  },
  tangible_assets: {
    table_type: TABLE_TYPES.plain,
    table_level: 0,
    table_name: `Tangible Assets`,
    available_rows: [`head`, `gross_book_value`, `a_and_d_rate`, `net_book_value`],
    head: {
      styles: {
        app: STYLES_COMBINATIONS.TITLE_GRAY_CELLS_TRANSPARENT
      },
      name: `Tangible Assets`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 0, type: `blank` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    gross_book_value: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Gross Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    a_and_d_rate: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Accumulated Depreciation`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    net_book_value: {
      styles: { app: STYLES_COMBINATIONS.LIGHT_BLUE },
      name: `Net Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    }
  },
  financial_assets: {
    table_type: TABLE_TYPES.plain,
    table_level: 0,
    table_name: `Financial Assets`,
    available_rows: [`head`, `gross_book_value`, `fair_value_adjustments`, `net_book_value`],
    head: {
      styles: {
        app: STYLES_COMBINATIONS.TITLE_GRAY_CELLS_TRANSPARENT
      },
      name: `Financial Assets`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 0, type: `blank` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    gross_book_value: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Gross Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    fair_value_adjustments: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Fair Value Adjustments`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    net_book_value: {
      styles: { app: STYLES_COMBINATIONS.LIGHT_BLUE },
      name: `Net Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    }
  },
  total_fixed_assets: {
    table_type: TABLE_TYPES.plain,
    table_level: 0,
    table_name: `Total Fixed Assets`,
    available_rows: [`head`, `gross_book_value`, `fair_value_adjustments`, `net_book_value`],
    head: {
      styles: {
        app: STYLES_COMBINATIONS.TITLE_BLACK_CELLS_TRANSPARENT
      },
      name: `Total Fixed Assets`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 0, type: `blank` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    gross_book_value: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `Gross Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    fair_value_adjustments: {
      styles: { app: STYLES_COMBINATIONS.TITLE_WHITE_CELLS_GRAY },
      name: `D&A and fair value adjustments`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    },
    net_book_value: {
      styles: { app: STYLES_COMBINATIONS.DARK_BLUE },
      name: `Net Book Value`,
      periods: [
        {
          period: `${PREV_YEAR}`,
          period_type: `historical`,
          fiscal_year: PREV_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3065.778, type: `currency` }
          }
        },
        {
          period: `${CURRENT_YEAR}`,
          period_type: `forecast`,
          fiscal_year: CURRENT_YEAR,
          fiscal_month: null,
          is_cxgr: false,
          is_yearly_view: true,
          values: {
            value: { value: 3025.0, type: `currency` }
          }
        }
      ],
      cxgr: null,
      is_yearly_view: true
    }
  }
}
